import { Provider, inject, observer } from 'mobx-react';
import React, { Fragment } from 'react';
import { translate, Interpolate } from 'react-i18next';
import PropTypes from 'prop-types';
import bem from 'b_';
import BrowserStore from '../../browser/stores/BrowserStore';
import Icon from '../Icon/Icon';
import { PfptWordsVertical } from '../Logo/PfptLogo';
import { TBody } from '../Text/Text';
import BrowserPopUp from '../BrowserPopUp/BrowserPopUp';
import FuseAlert from '../FuseControls/FuseAlert';
import debug from '../../debug';
import { LOCATION_ORIGIN } from '../../env';
import initData from '../../browser/browserInitData';
import initBrowserStores from '../../browser/initBrowserStores';
import TopBarAlternative from '../TopBarAlternative/TopBarAlternative';
import FlagStore from '../../stores/FlagStore';
import { isFuse5Enabled } from '../../fuseFeatureFlag';
import OldBrowserWelcome from './oldBrowserWelcome';
import FuseButton from '../FuseControls/FuseButton';
import './BrowserWelcome.css';

const block = bem.lock('NewBrowserWelcome');

function Step({ title, children, number, selected, onClick }) {
    const id = `welcome_steps_${number}`;

    return <div className={block('Step', { active: number === selected })}>
        <FuseButton
            htmlFor={id}
            data-number={number}
            className={block('Title')}
            wrapperClassName={block('TitleWrapper')}
            onClick={() => onClick(number)}
        >
            {title}
        </FuseButton>
        <TBody className={block('Content')}>
            {children}
        </TBody>
    </div>;
}

Step.propTypes = {
    title: PropTypes.node.isRequired,
    number: PropTypes.number.isRequired,
    selected: PropTypes.number.isRequired,
    onClick: PropTypes.func.isRequired,
};

function getDefaultActivationLink() {
    return {
        value: '',
        isError: false,
    };
}



function ActivateDifferentBrowserPopup({ isOpen, onClose, t }) {
    const title = t('BrowserWelcome.activateDifferentBrowserModal.title');

    return <BrowserPopUp
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        footerActions={
            <FuseButton
                type="primary"
                color="blue"
                onClick={onClose}
            >
                <Interpolate i18nKey="BrowserWelcome.activateDifferentBrowserPopup.okButton" />
            </FuseButton>
        }
    >
        <TBody>
            <div style={{ fontWeight: 700 }}>
                <Interpolate i18nKey="BrowserWelcome.activateDifferentBrowserModal.listCopied" />
            </div>
            <br />
            <div>
                {t('NewBrowserWelcome.activateDifferentBrowserPopup.content', { defaultValue: null }) ? (
                    <Interpolate
                        i18nKey="BrowserWelcome.activateDifferentBrowserPopup.content"
                        boldText={<span style={{ fontWeight: 700 }}>
                            <Interpolate i18nKey="BrowserWelcome.activateDifferentBrowserPopup.boldText" />
                        </span>}
                    />
                ) : (
                    <Fragment>
                        <Interpolate i18nKey="BrowserWelcome.activateDifferentBrowserModal.listSwitch" />
                        {' '}
                        <Interpolate i18nKey="BrowserWelcome.activateDifferentBrowserModal.listPaste" />
                    </Fragment>
                )}
            </div>
        </TBody>

        <FuseAlert type="info">
            <Interpolate
                i18nKey="BrowserWelcome.activateDifferentBrowserModal.noteText"
                generateLinkButtonName={<Interpolate i18nKey="BrowserWelcome.activationStep.newGenerateLink" />}
            />
        </FuseAlert>
    </BrowserPopUp>;
}

ActivateDifferentBrowserPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

function parseUserAgentIntoBrowser(userAgent) {
    if (!userAgent) {
        return null;
    }

    if (userAgent.includes('rv:11.0')) {
        return {
            icon: 'browser_ie',
            name: 'Internet Explorer 11',
        };
    }

    const hasChrome = userAgent.includes('Chrome/');
    const hasEdge = userAgent.includes('Edge') || userAgent.includes(' Edg/');

    if (hasChrome && !hasEdge) {
        return {
            icon: 'browser_chrome',
            name: 'Google Chrome',
        };
    }

    if (hasChrome && hasEdge) {
        return {
            icon: 'browser_edge',
            name: 'Microsoft Edge',
        };
    }

    if (userAgent.includes('Macintosh') && userAgent.includes('Safari') && !hasChrome && !hasEdge) {
        return {
            icon: 'browser_safari',
            name: 'Safari',
        };
    }

    if (userAgent.includes('Firefox') && !hasEdge) {
        return {
            icon: 'browser_firefox',
            name: 'Mozilla Firefox',
        };
    }

    return null;
}

class NewBrowserWelcome extends React.Component {
    static propTypes = {
        browser: BrowserStore.PropType.isRequired,
        selected: PropTypes.number,
        t: PropTypes.func.isRequired,
    };

    state = {
        activationLink: getDefaultActivationLink(),
        selected: this.props.selected || 1,
        currentBrowser: parseUserAgentIntoBrowser(navigator.userAgent),
        isActivateAnotherBrowserPopupOpened: false,
    };

    isUnmounted = false;

    mockStoresForDemoTopBar = initBrowserStores({
        browser: {
            ...initData.stores.browser,
            pageStatus: BrowserStore.PAGE_STATUS.SECURE_SITE,
            isHomePage: false,
            topBarUnpinned: new FlagStore(false),
            topBarNeedsMinimization: new FlagStore(true),
            adBlockSwitchEnabled: this.props.browser.adBlockSwitchEnabled,
        },
    }, false);

    componentDidMount() {
        this.getToken();
    }

    componentWillUnmount() {
        this.isUnmounted = true;
    }

    getToken() {
        if (this.props.browser.isAutoProvisioned) {
            return;
        }

        this.setState({
            activationLink: getDefaultActivationLink(),
        });

        this.props.browser.getActivationToken()
            .then(({ value }) => {
                if (this.isUnmounted) {
                    return;
                }
                try {
                    this.setState({
                        activationLink: {
                            value: `${LOCATION_ORIGIN}/register?token=${value}`,
                            isError: false,
                        },
                    });
                } catch (e) {
                    debug.recordError(e);
                }
            })
            .catch((e) => {
                if (!this.isUnmounted) {
                    this.setState({
                        activationLink: {
                            value: '',
                            isError: true,
                        },
                    });
                }
                debug.warn(e);
            });
    }

    copyText() {
        const scrollTop = window.pageYOffset;
        this.input.focus();
        this.input.select();
        let succeed = false;
        try {
            succeed = document.execCommand('copy');
        } catch (e) {
            succeed = false;
        }
        this.input.blur();
        if (!succeed) {
            debug.recordError(new Error('BrowserWelcome.copyText: Copy failed'));
        }
        if (scrollTop !== window.pageYOffset) {
            // cause selection change causes scroll sometimes
            window.pageYOffset = scrollTop;
        }
    }

    copyInputRef = (node) => {
        this.input = node;
    };

    onCopyClick = () => {
        this.copyText();

        this.setState({
            isActivateAnotherBrowserPopupOpened: true,
        });

        this.getToken();
    };

    onPopupClose = () => {
        this.setState({
            isActivateAnotherBrowserPopupOpened: false,
        });
    };

    onSelect = (selected) => {
        this.setState({ selected });
    };

    onDone = () => {
        this.props.browser.setHomePageTabId(0);
    };

    renderActivationStep = (stepIndex) => {
        return <Step
            title={<Interpolate i18nKey="BrowserWelcome.activationStep.newTitle" />}
            number={stepIndex}
            selected={this.state.selected}
            onClick={this.onSelect}
        >
            <div className={block('StepText')}>
                <Interpolate i18nKey="BrowserWelcome.activationStep.congratulationText" />
            </div>
            <div className={block('StepContentCentered')} style={{ marginTop: '16px' }}>
                {this.state.currentBrowser ? <div className={block('Browser')}>
                    <Interpolate
                        i18nKey="BrowserWelcome.activationStep.currentBrowser"
                        className={block('BrowserWithIcon')}
                        browser={<Fragment>
                            <Icon
                                type={this.state.currentBrowser.icon}
                                className={block('BrowserIcon')}
                            />
                            <span>{this.state.currentBrowser.name}</span>
                        </Fragment>}
                    />
                </div> : null}
                <FuseButton type={'primary'} color={'blue'} onClick={() => this.onSelect(stepIndex + 1)}>
                    <Interpolate i18nKey="BrowserWelcome.activationStep.continueWithCurrentBrowser" />
                </FuseButton>
                {!this.state.activationLink.isError ? <Fragment>
                    <div className={block('Or')}>
                        <Interpolate i18nKey="BrowserWelcome.activationStep.or" />
                    </div>
                    <input
                        readOnly
                        type="text"
                        tabIndex={-1}
                        value={this.state.activationLink.value}
                        ref={this.copyInputRef}
                        className={block('CopyInput')}
                        aria-hidden="true"
                    />
                    <div className={block('ActivateDifferentBrowser')}>
                        <Interpolate i18nKey="BrowserWelcome.activationStep.activateDifferentBrowser" />
                        <FuseButton
                            type={'secondary'}
                            color={'blue'}
                            loading={!this.state.activationLink.value}
                            onClick={this.onCopyClick}
                            style={{ margin: '0 16px' }}
                        >
                            <Interpolate i18nKey="BrowserWelcome.activationStep.newGenerateLink" />
                        </FuseButton>
                        <Interpolate i18nKey="BrowserWelcome.activationStep.oneTimeActivationLink" />
                    </div>
                </Fragment> : null}
            </div>
        </Step>;
    };

    renderUIStep = () => (stepIndex) => {
        return <Step
            title={<Interpolate i18nKey="BrowserWelcome.uiStep.title" />}
            number={stepIndex}
            selected={this.state.selected}
            onClick={this.onSelect}
        >
            <div className={block('StepText')}>
                <Interpolate i18nKey="BrowserWelcome.uiStep.text" />
            </div>
            <div className={block('StepContentCentered')}>
                <div className={block('TopBarWrapper')}>
                    <Provider {...this.mockStoresForDemoTopBar}>
                        <div style={{ position: 'relative' }}>
                            <TopBarAlternative isWizard />
                        </div>
                    </Provider>
                </div>
                <FuseButton
                    type={'primary'}
                    color={'blue'}
                    onClick={() => this.onSelect(stepIndex + 1)}
                    style={{ minWidth: 97 }}
                >
                    <Interpolate i18nKey="BrowserWelcome.continueButton" />
                </FuseButton>
            </div>
        </Step>;
    };

    renderBookmarkStep = (stepIndex) => {
        return <Step
            title={<Interpolate i18nKey="BrowserWelcome.bookmarkStep.title" />}
            number={stepIndex}
            selected={this.state.selected}
            onClick={this.onSelect}
        >
            <div className={block('StepText')}>
                {/* Check if split text keys exist (English), otherwise fallback to single text key for non-English languages */}
                {this.props.t('NewBrowserWelcome.bookmarkStep.textLine1', { defaultValue: null }) ? (
                    <>
                        <div>
                            <Interpolate i18nKey="BrowserWelcome.bookmarkStep.textLine1" />
                        </div>
                        <div style={{ marginTop: '16px' }}>
                            <strong>Otherwise you will need to remember to use</strong> -{' '}
                            <a style={{ color: '#03A9F4' }} href={LOCATION_ORIGIN}>{LOCATION_ORIGIN}</a>
                        </div>
                    </>
                ) : (
                    <Interpolate
                        i18nKey="BrowserWelcome.bookmarkStep.text"
                        link={<a style={{ color: '#03A9F4' }} href={LOCATION_ORIGIN}>{LOCATION_ORIGIN}</a>}
                    />
                )}
            </div>
            <div className={block('StepContentCentered')} style={{ marginTop: '16px' }}>
                <div style={{ marginBottom: '16px' }}>
                    <Interpolate i18nKey="BrowserWelcome.bookmarkStep.shortcutText" />
                </div>
                <FuseButton
                    type={'primary'}
                    color={'blue'}
                    onClick={this.onDone}
                    style={{ minWidth: 88 }}
                >
                    <Interpolate i18nKey="BrowserWelcome.doneButton" />
                </FuseButton>
            </div>
        </Step>;
    };

    render() {
        const { browser } = this.props;
        const steps = browser.isAutoProvisioned
            ? [this.renderUIStep(browser), this.renderBookmarkStep]
            : [this.renderActivationStep, this.renderUIStep(browser), this.renderBookmarkStep];

        return <div className={block()}>
            <div className={block('Inner')}>
                <ActivateDifferentBrowserPopup
                    isOpen={this.state.isActivateAnotherBrowserPopupOpened}
                    onClose={this.onPopupClose}
                    t={this.props.t}
                />
                <PfptWordsVertical className={block('Header')} />
                <div className={block('List')}>
                    {steps.map((render, i) => <Fragment key={i}>{render(i + 1)}</Fragment>)}
                </div>
            </div>
        </div>;
    }
}

const WrappedNewBrowserWelcome = translate()(inject('browser')(observer(NewBrowserWelcome)));

const BrowserWelcome = (props) => {
    return isFuse5Enabled() ? <WrappedNewBrowserWelcome {...props} /> : <OldBrowserWelcome {...props} />;
};

export default BrowserWelcome;
